/**
 * Guardrail Store
 * 
 * Manages AI guardrail configuration state:
 * - Guardrail CRUD operations
 * - Simulation engine
 * - Coverage analysis
 * - Conflict detection
 * 
 * Peer reviewed: Gemini 3 Pro, GPT-4o
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { 
  Guardrail, 
  GuardrailCategory, 
  ActionType,
  Condition,
} from '../types/guardrails';

// ============================================================================
// Simulation Types
// ============================================================================

export interface GuardrailSimulationDetail {
  guardrailId: string;
  guardrailName: string;
  actionTaken: ActionType;
  processingTimeMs: number;
  matchedConditions?: string[] | undefined;
  matches?: string[] | undefined;
  userMessage?: string | undefined;
}

export interface GuardrailSimulationResult {
  status: 'allowed' | 'blocked' | 'redacted' | 'alerted' | 'logged';
  totalProcessingTimeMs: number;
  rulesEvaluated: number;
  details: GuardrailSimulationDetail[];
  sanitizedOutput?: string | undefined;
  confidenceScore?: number | undefined;
}

// ============================================================================
// State Interface
// ============================================================================

interface GuardrailState {
  guardrails: Map<string, Guardrail>;
  selectedGuardrailId: string | null;
  isLoading: boolean;
  isSimulating: boolean;
  error: string | null;
  categoryFilter: GuardrailCategory | 'all';
  searchQuery: string;
}

interface GuardrailActions {
  setGuardrails: (guardrails: Guardrail[]) => void;
  setSelectedGuardrailId: (id: string | null) => void;
  addGuardrail: (guardrail: Guardrail) => void;
  updateGuardrail: (id: string, updates: Partial<Guardrail>) => void;
  deleteGuardrail: (id: string) => void;
  toggleGuardrailEnabled: (id: string) => void;
  setCategoryFilter: (category: GuardrailCategory | 'all') => void;
  setSearchQuery: (query: string) => void;
  clearSelection: () => void;
  runSimulation: (input: string, guardrailIds?: string[]) => Promise<GuardrailSimulationResult>;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: GuardrailState = {
  guardrails: new Map(),
  selectedGuardrailId: null,
  isLoading: false,
  isSimulating: false,
  error: null,
  categoryFilter: 'all',
  searchQuery: '',
};

// ============================================================================
// Store
// ============================================================================

export const useGuardrailStore = create<GuardrailState & GuardrailActions>()(
  devtools(
    subscribeWithSelector(
      immer((set, get) => ({
        ...initialState,

        setGuardrails: (guardrails: Guardrail[]) => {
          set((state) => {
            state.guardrails.clear();
            guardrails.forEach(g => state.guardrails.set(g.id, g));
          });
        },

        setSelectedGuardrailId: (id: string | null) => {
          set({ selectedGuardrailId: id });
        },

        addGuardrail: (guardrail: Guardrail) => {
          set((state) => {
            state.guardrails.set(guardrail.id, guardrail);
          });
        },

        updateGuardrail: (id: string, updates: Partial<Guardrail>) => {
          set((state) => {
            const existing = state.guardrails.get(id);
            if (existing) {
              state.guardrails.set(id, { ...existing, ...updates });
            }
          });
        },

        deleteGuardrail: (id: string) => {
          set((state) => {
            state.guardrails.delete(id);
            if (state.selectedGuardrailId === id) {
              state.selectedGuardrailId = null;
            }
          });
        },

        toggleGuardrailEnabled: (id: string) => {
          set((state) => {
            const guardrail = state.guardrails.get(id);
            if (guardrail) {
              guardrail.enabled = !guardrail.enabled;
            }
          });
        },

        setCategoryFilter: (category: GuardrailCategory | 'all') => {
          set({ categoryFilter: category });
        },

        setSearchQuery: (query: string) => {
          set({ searchQuery: query });
        },

        clearSelection: () => {
          set({ selectedGuardrailId: null });
        },

        runSimulation: async (input: string, guardrailIds?: string[]): Promise<GuardrailSimulationResult> => {
          set({ isSimulating: true, error: null });
          
          try {
            const state = get();
            const guardrailsToTest = guardrailIds 
              ? guardrailIds.map(id => state.guardrails.get(id)).filter((g): g is Guardrail => !!g && g.enabled)
              : Array.from(state.guardrails.values()).filter(g => g.enabled);
            
            // Sort by priority
            guardrailsToTest.sort((a, b) => a.priority - b.priority);
            
            const startTime = performance.now();
            const details: GuardrailSimulationDetail[] = [];
            let finalStatus: GuardrailSimulationResult['status'] = 'allowed';
            let sanitizedOutput = input;
            
            // Process each guardrail
            for (const guardrail of guardrailsToTest) {
              const ruleStartTime = performance.now();
              let triggered = false;
              const matches: string[] = [];
              
              // Check conditions
              for (const item of guardrail.conditions.items) {
                if ('field' in item) {
                  const condition = item;
                  const textToCheck = condition.field === 'prompt' ? input : input;
                  
                  let match = false;
                  if (condition.matchType === 'keyword' && typeof condition.value === 'string') {
                    match = textToCheck.toLowerCase().includes(condition.value.toLowerCase());
                    if (match) {matches.push(condition.value);}
                  } else if (condition.matchType === 'regex' && typeof condition.value === 'string') {
                    try {
                      const regex = new RegExp(condition.value, 'gi');
                      const regexMatches = textToCheck.match(regex);
                      match = !!regexMatches;
                      if (regexMatches) {matches.push(...regexMatches);}
                    } catch {
                      // Invalid regex
                    }
                  } else if (condition.matchType === 'pattern' && typeof condition.value === 'string') {
                    const patterns: Record<string, RegExp> = {
                      ssn: /\b\d{3}-\d{2}-\d{4}\b/g,
                      'credit-card': /\b(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|3[47][0-9]{13})\b/g,
                      email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
                      'aws-key': /\b(?:AKIA|ABIA|ACCA|ASIA)[0-9A-Z]{16}\b/g,
                      'private-ip': /\b(?:10\.\d{1,3}|172\.(?:1[6-9]|2\d|3[01])|192\.168)\.\d{1,3}\.\d{1,3}\b/g,
                    };
                    const patternRegex = patterns[condition.value];
                    if (patternRegex) {
                      const patternMatches = textToCheck.match(patternRegex);
                      match = !!patternMatches;
                      if (patternMatches) {matches.push(...patternMatches);}
                    }
                  }
                  
                  if (condition.operator === 'not-contains' || condition.operator === 'not-matches') {
                    match = !match;
                  }
                  
                  if (match) {
                    triggered = true;
                  }
                }
              }
              
              const ruleEndTime = performance.now();
              
              if (triggered) {
                const primaryAction = guardrail.actions[0];
                if (primaryAction) {
                  details.push({
                    guardrailId: guardrail.id,
                    guardrailName: guardrail.name,
                    actionTaken: primaryAction.type,
                    processingTimeMs: ruleEndTime - ruleStartTime,
                    matchedConditions: guardrail.conditions.items
                      .filter((item): item is Condition => 'field' in item)
                      .map(c => `${c.field} ${c.operator} ${c.value}`),
                    matches,
                    userMessage: primaryAction.userMessage,
                  });
                  
                  if (primaryAction.type === 'block' && finalStatus !== 'blocked') {
                    finalStatus = 'blocked';
                  } else if (primaryAction.type === 'redact' && finalStatus === 'allowed') {
                    finalStatus = 'redacted';
                    matches.forEach(m => {
                      sanitizedOutput = sanitizedOutput.replace(
                        new RegExp(m.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
                        primaryAction.redactionChar ?? '***'
                      );
                    });
                  } else if (primaryAction.type === 'alert' && finalStatus === 'allowed') {
                    finalStatus = 'alerted';
                  } else if (primaryAction.type === 'log' && finalStatus === 'allowed') {
                    finalStatus = 'logged';
                  }
                }
              }
            }
            
            const endTime = performance.now();
            
            const result: GuardrailSimulationResult = {
              status: finalStatus,
              totalProcessingTimeMs: endTime - startTime,
              rulesEvaluated: guardrailsToTest.length,
              details,
              sanitizedOutput: finalStatus === 'redacted' ? sanitizedOutput : undefined,
              confidenceScore: details.length > 0 ? 0.95 : 1.0,
            };
            
            set({ isSimulating: false });
            
            return result;
          } catch (err) {
            set({ isSimulating: false, error: (err as Error).message });
            throw err;
          }
        },
      }))
    ),
    { name: 'guardrail-store' }
  )
);

// ============================================================================
// Selectors
// ============================================================================

type StoreState = GuardrailState & GuardrailActions;

export const selectGuardrails = (state: StoreState) => state.guardrails;

export const selectSelectedGuardrail = (state: StoreState) =>
  state.selectedGuardrailId ? state.guardrails.get(state.selectedGuardrailId) : undefined;

export const selectSelectedGuardrailId = (state: StoreState) =>
  state.selectedGuardrailId;

export const selectGuardrailById = (state: StoreState, id: string) =>
  state.guardrails.get(id);

export const selectIsLoading = (state: StoreState) => state.isLoading;
export const selectIsSimulating = (state: StoreState) => state.isSimulating;
export const selectError = (state: StoreState) => state.error;
export const selectCategoryFilter = (state: StoreState) => state.categoryFilter;
export const selectSearchQuery = (state: StoreState) => state.searchQuery;

export const selectCoverage = (state: StoreState): Record<GuardrailCategory, number> => {
  const coverage: Record<GuardrailCategory, number> = {
    'data-protection': 0,
    compliance: 0,
    security: 0,
    operational: 0,
  };
  
  state.guardrails.forEach(g => {
    if (g.enabled) {
      coverage[g.category]++;
    }
  });
  
  return coverage;
};

export const selectFilteredGuardrails = (state: StoreState) => {
  let result = Array.from(state.guardrails.values());
  
  if (state.categoryFilter !== 'all') {
    result = result.filter(g => g.category === state.categoryFilter);
  }
  
  if (state.searchQuery.trim()) {
    const query = state.searchQuery.toLowerCase();
    result = result.filter(g =>
      g.name.toLowerCase().includes(query) ||
      g.description.toLowerCase().includes(query) ||
      g.tags.some(t => t.toLowerCase().includes(query))
    );
  }
  
  return result.sort((a, b) => a.priority - b.priority);
};

export const selectGuardrailStats = (state: StoreState) => {
  const all = Array.from(state.guardrails.values());
  return {
    total: all.length,
    enabled: all.filter(g => g.enabled).length,
    byCategory: {
      'data-protection': all.filter(g => g.category === 'data-protection').length,
      compliance: all.filter(g => g.category === 'compliance').length,
      security: all.filter(g => g.category === 'security').length,
      operational: all.filter(g => g.category === 'operational').length,
    },
  };
};

export default useGuardrailStore;
